import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { usePopover } from "./usePopover";

/**
 * A dropdown menu for row actions and the account button.
 *
 * The floating-panel mechanics - outside-click, Escape, focus return, and the
 * portal that keeps the panel out of the table's `overflow` box - live in
 * `usePopover`, shared with `FilterSelect`. Read that file for why each of them
 * is there; every one has a specific bug behind it.
 *
 * Deliberately NOT a roving-focus implementation. These menus hold two or three
 * items, every one of them is a real button in the tab order, and the arrow-key
 * behaviour of a full menubar widget would be more code than the feature.
 */
export function Menu({
  trigger,
  children,
  align = "end",
  className,
  panelClassName,
}: {
  /** Rendered inside the trigger button. */
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: "start" | "end";
  className?: string;
  panelClassName?: string;
}) {
  const { open, toggle, close, rootRef, triggerRef, panelRef, panelStyle, panelTarget } =
    usePopover({
      align,
    });

  return (
    <div ref={rootRef} className={cn("menu", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        className="menu__trigger"
      >
        {trigger}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            // See the "top layer" note in `usePopover.ts` - this is what lets
            // the panel render over an open `Modal`/`Drawer`.
            popover="manual"
            role="menu"
            // Any activation inside closes the menu. Bubbling from the item's
            // own handler means a single onClick per item rather than every call
            // site remembering to close.
            onClick={() => close()}
            style={panelStyle}
            className={cn("menu__panel", panelClassName)}
          >
            {children}
          </div>,
          panelTarget
        )}
    </div>
  );
}

export function MenuItem({
  children,
  icon,
  tone = "default",
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone?: "default" | "danger";
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn("menu__item", tone === "danger" && "menu__item--danger")}
    >
      {icon}
      {children}
    </button>
  );
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return <p className="menu__label">{children}</p>;
}

export function MenuSeparator() {
  return <hr className="menu__separator" />;
}
